import Provide from '@/components/Provide'
import { filter, sortBy } from 'lodash'
import Vue, { VNode } from 'vue'
import { ScopedSlotReturnValue } from 'vue/types/vnode'

const identity = (x: any) => x

interface Props {
  beforeSave: (x: any) => any
  afterSave: (x: any) => any
  default?: any
  removeWith?: string | ((x: any) => Promise<any>)
  from: string | Iterable<any>
  revert: boolean
  saveWith?: string | ((x: any) => Promise<any>)
  sort?: object | (() => any) | any[]
  tag: string
  where?: object | (() => boolean) | string
}

export default Vue.extend<Props>({
  functional: true,
  name: 'Artifacts',
  render(
    h,
    {
      scopedSlots,
      parent: { $store },
      props: {
        afterSave,
        beforeSave,
        default: defaultValue,
        removeWith,
        from,
        revert,
        saveWith,
        sort,
        where,
        tag: Tag,
      },
      data: { attrs },
    }
  ): VNode[] {
    // fetch element from the store
    // ----------------------------
    let elements: any[] = []

    if (typeof from === 'string') {
      // dynamic store getter
      if (typeof $store.getters[from] === 'function') {
        if (Array.isArray(where)) {
          elements = $store.getters[from](...where)
        } else if (where) {
          elements = $store.getters[from](where)
        } else {
          elements = $store.getters[from]()
        }
      }
      // static store getter
      else {
        elements = $store.getters[from]
      }
    } else {
      // this.from is not a string
      elements = Array.from(from)
    }

    if (where) {
      elements = filter(elements, where)
    }
    if (sort) {
      elements = sortBy(elements, sort)
    }
    if (revert) {
      elements = elements.reverse()
    }

    // setup a save function
    // ---------------------
    const save = async (payload: object) => {
      const adjustedPayload = await beforeSave(payload)
      if (typeof saveWith === 'string') {
        const saved = await $store.dispatch({
          type: saveWith,
          ...adjustedPayload,
        })
        await afterSave(saved)
        return saved
      } else if (saveWith) {
        const saved = await saveWith(adjustedPayload)
        await afterSave(saved)
        return saved
      } else {
        throw new Error('save function not given')
      }
    }

    const remove = async (payload: object) => {
      if (typeof removeWith === 'string') {
        return await $store.dispatch({
          type: removeWith,
          ...payload,
        })
      } else if (removeWith) {
        return await removeWith(payload)
      } else {
        throw new Error('remove function not given')
      }
    }

    if (scopedSlots.default) {
      return [
        <Tag {...attrs}>
          {scopedSlots.default({
            elements,
            from,
            remove,
            save,
            where,
          })}
        </Tag>,
      ]
    }

    if (scopedSlots.element && elements.length > 0) {
      const elementSlot = scopedSlots.element
      return elements.map(element => (
        <Provide
          {...{
            tag: Tag,
            key: element.id,
            attrs: { ...attrs, save, remove, element },
          }}>
          {elementSlot({ element, save, remove })}
        </Provide>
      ))
    }

    if (scopedSlots.element && defaultValue) {
      const elementSlot = scopedSlots.element
      return [
        <Provide
          {...{
            attrs: { ...attrs, save, element: defaultValue },
            key: 'default',
            tag: Tag,
          }}>
          {elementSlot({ element: defaultValue, save })}
        </Provide>,
      ]
    }

    if (scopedSlots.ifNotFound) {
      return [
        <Provide {...{ tag: Tag, key: 'notFound', attrs: { ...attrs, save } }}>
          {scopedSlots.ifNotFound({
            from,
            save,
            where,
          })}
        </Provide>,
      ]
    }

    return [<Tag {...attrs} />]
  },
  props: {
    afterSave: {
      default: (x: any) => x,
      type: (Function as unknown) as () => (x: any) => any,
    },
    beforeSave: {
      default: (x: any) => x,
      type: (Function as unknown) as () => (x: any) => any,
    },
    default: {
      type: Object,
    },
    from: {
      type: [String, Array],
    },
    removeWith: {
      type: [String, (Function as unknown) as () => (x: any) => Promise<any>],
    },
    revert: {
      type: Boolean,
    },
    saveWith: {
      type: [String, (Function as unknown) as () => (x: any) => Promise<any>],
    },
    sort: {
      type: [Object, Function, Array, String],
    },
    tag: {
      default: 'div',
      type: String,
    },
    where: {
      type: [Function, Object, String],
    },
  },
})
